(ns ui.view.entity-graph.model
  (:require
   [common.schema.entities.graph-reference :as gref]))

(def WIDTH 240)
(def HEIGHT 100)
(def PADDING 24)

(def graph-icons
  {:entity.type/graph-company :fa-building
   :entity.type/graph-person :fa-user
   :entity.type/graph-fund :fa-sack-dollar
   :entity.type/graph-fof-asset :fa-seedling})

(def path-types
  [{:value :bezier :label "Bezier"}
   {:value :straight :label "Straight"}
   {:value :smooth-step :label "Smooth-step"}])

(def default-source-handle-by-type
  (->> gref/type-defs
       (filterv :connector?)
       (mapv (fn [{:keys [key default]}]
               [key (:source-handle default)]))
       (into {})))

(def default-target-handle-by-type
  (->> gref/type-defs
       (filterv :connector?)
       (mapv (fn [{:keys [key default]}]
               [key (:target-handle default)]))
       (into {})))

(def default-line-type-by-type
  (->> gref/type-defs
       (filterv :connector?)
       (mapv (fn [{:keys [key default]}]
               [key (:line-type default)]))
       (into {})))

(def source-handle-options
  [{:value :bottom :label "From bottom"}
   {:value :right :label "From right"}
   {:value :left :label "From left"}
   {:value :top :label "From top"}])

(def target-handle-options
  [{:value :top :label "To top"}
   {:value :left :label "To left"}
   {:value :right :label "To right"}
   {:value :bottom :label "To bottom"}])

(def ownerships-config :system-view-config/ownerships)
(def gps-and-lps-config :system-view-config/gps-and-lps)
(def lps-config :system-view-config/lps)

(def system-level-configs
  {ownerships-config
   {:hide-connector-types
    #{:graph-reference.type/gp
      :graph-reference.type/lp}
    :config
    {}}
   gps-and-lps-config
   {:hide-connector-types
    #{:graph-reference.type/ownership}
    :config
    {}}
   lps-config
   {:hide-connector-types
    #{:graph-reference.type/gp
      :graph-reference.type/ownership}
    :config
    {}}})

;; State structure related keys and paths:
(def root ::state)
(def root-path [root])

;; root path of an instance of main-entity-id specific state
(defn i-root
  [{:keys [main-entity-id]}]
  (conj root-path main-entity-id))

(def ephemeral-key :ephemeral)
(def ephemeral-path [ephemeral-key])
(defn ephemeral-full-path
  [ctx]
  (conj (i-root ctx) ephemeral-key))

(def ephemeral-positions-key :positions)
(def ephemeral-positions-path [ephemeral-positions-key])
(defn ephemeral-positions-full-path
  [ctx]
  (conj (ephemeral-full-path ctx) ephemeral-positions-key))

(def ephemeral-hide-rules-key :hide-rules)
(def ephemeral-hide-rules-path [ephemeral-hide-rules-key])
(defn ephemeral-hide-rules-full-path
  [ctx]
  (conj (ephemeral-full-path ctx) ephemeral-hide-rules-key))

;; generalizing the pattern above:
(defn extend-path
  [base-path-fn new-key]
  (fn extend-path-fn [ctx]
    (conj (base-path-fn ctx) new-key)))

(def synthetic-timestamp-key
  :synthetic-timestamp)

;; for now this is common to all entity-graph-view-instances.
;; ctx still passed for future-proofing:
(defn synthetic-timestamp-full-path
  [_ctx]
  (conj root-path synthetic-timestamp-key))

(defn ws-sub-key
  [{:keys [main-entity-id] :as _ctx}]
  (keyword (str "entity-graph-" main-entity-id)))

(def creating-key :creating?)
(def creating-full-path
  (extend-path i-root creating-key))

(def saving-view-config-key :saving?)
(def saving-view-config-full-path
  (extend-path i-root saving-view-config-key))

(def selecting-key :selecting)
(def selecting-full-path
  (extend-path i-root selecting-key))

(def show-all-gref-sections-key :show-all-gref-sections)
(def show-all-gref-sections-full-path
  (extend-path i-root show-all-gref-sections-key))

(def error-key :error)
(def error-full-path
  (extend-path i-root error-key))

(def ready-key :ready?)
(def ready-full-path
  (extend-path i-root ready-key))

(def provider-key :provider)
(def provider-full-path
  (extend-path i-root provider-key))

(def loaded-key :loaded?)
(def loaded-full-path
  (extend-path i-root loaded-key))

(def delete-modal-key :delete-modal)
(def delete-modal-full-path
  (extend-path i-root delete-modal-key))

(def create-connector-modal-key :create-connector-modal)
(def create-connector-modal-full-path
  (extend-path i-root create-connector-modal-key))

(def create-connector-edits-key :edits)
(def create-connector-edits-full-path
  (extend-path create-connector-modal-full-path create-connector-edits-key))

(def node-menu-key :node-menu)
(def node-menu-full-path
  (extend-path i-root node-menu-key))

(def edge-menu-key :edge-menu)
(def edge-menu-full-path
  (extend-path i-root edge-menu-key))

(def selected-nodes-key :selected-nodes)
(def selected-nodes-path [selected-nodes-key])
(def selected-nodes-full-path
  (extend-path ephemeral-full-path selected-nodes-key))

(def selected-edges-key :selected-edges)
(def selected-edges-path [selected-edges-key])
(def selected-edges-full-path
  (extend-path ephemeral-full-path selected-edges-key))

(def sidebar-mode-key :sidebar-mode)
(def sidebar-mode-path [sidebar-mode-key])
(def sidebar-mode-full-path
  (extend-path ephemeral-full-path sidebar-mode-key))

(def viewed-key :viewed)
(def viewed-path [viewed-key])
(def viewed-full-path
  (extend-path ephemeral-full-path viewed-key))

(def settings-key :settings)
(def settings-path [settings-key])
(def settings-full-path
  (extend-path ephemeral-full-path settings-key))

(def view-config-key :view-config)
(def view-config-path [view-config-key])
(def view-config-full-path
  (extend-path i-root view-config-key))

(def selected-system-config-key :selected-system-config)
(def selected-system-config-path [selected-system-config-key])
(def selected-system-config-full-path
  (extend-path i-root selected-system-config-key))

(def manual-positions-key :manual-positions?)
(def manual-positions-path [manual-positions-key])
(def manual-positions-full-path
  (extend-path view-config-full-path manual-positions-key))

(def sidebar-pinned-key :sidebar-pinned?)
(def sidebar-pinned-path [sidebar-pinned-key])
(def sidebar-pinned-full-path
  (extend-path view-config-full-path sidebar-pinned-key))

(def view-positions-key :positions)
(def view-positions-path [view-positions-key])
(def view-positions-full-path
  (extend-path view-config-full-path view-positions-key))

(def hide-rules-key :hide-rules)
(def hide-rules-path [hide-rules-key])
(def hide-rules-full-path
  (extend-path view-config-full-path hide-rules-key))

(def defaults-key :defaults)
(def defaults-path [defaults-key])
(def defaults-full-path
  (extend-path view-config-full-path defaults-key))

(def path-type-key :path-type)
(def path-type-path [path-type-key])
(def path-type-full-path
  (extend-path defaults-full-path path-type-key))

(def highlight-pep-key :highlight-pep?)
(def highlight-pep-path [highlight-pep-key])
(def highlight-pep-full-path
  (extend-path defaults-full-path highlight-pep-key))

(def show-icons-key :show-icons?)
(def show-icons-path [show-icons-key])
(def show-icons-full-path
  (extend-path defaults-full-path show-icons-key))

(def hide-grid-key :hide-grid?)
(def hide-grid-path [hide-grid-key])
(def hide-grid-full-path
  (extend-path defaults-full-path hide-grid-key))

(def source-handle-key :source-handle)
(def target-handle-key :target-handle)

(def line-type-key :line-type)

;; TODO default-label-positions, extend defaults-full-path
(def label-positions-key :label-positions)
(def label-positions-path [label-positions-key])
(def label-positions-full-path
  (extend-path view-config-full-path label-positions-key))

(def hiding-view-key :hiding-view?)
(def hiding-view-path [hiding-view-key])
(def hiding-view-full-path
  (extend-path ephemeral-full-path hiding-view-key))

(def edit-map-profile-key :edit-map-profile-modal)
(def edit-map-profile-path [edit-map-profile-key])
(def edit-map-profile-full-path
  (extend-path i-root edit-map-profile-key))

(def edit-connector-key :edit-connector-modal)
(def edit-connector-path [edit-connector-key])
(def edit-connector-full-path
  (extend-path i-root edit-connector-key))

(def reconnecting-key :reconnecting)
(def reconnecting-path [reconnecting-key])
(def reconnecting-full-path
  (extend-path ephemeral-full-path reconnecting-key))

;; Subscriptions
(def root-state ::state)
(def state ::state-instance)

(def ready? ::ready?)
(def loaded? ::loaded?)

(def provider ::provider)

(def has-pep-attribute? ::has-pep-attribute?)

;;(def main-entity ::main-entity)
(def main-map-profile ::main-map-profile)
(def entity-map-profile ::entity-map-profile)

(def creating ::creating)

(def sidebar-pinned? ::sidebar-pinned?)
(def sidebar-collapsed? ::sidebar-collapsed?)
(def sidebar-mode ::sidebar-mode)

(def selecting? ::selecting?)
(def show-all-gref-sections ::show-all-grefs)

(def ephemeral ::ephemeral)
;; short-term node positions - during dragging
(def ephemeral-positions ::ephemeral-positions)
(def display-visibility-changes? ::display-visibility-changes?)
(def selected-nodes ::selected-nodes)
(def selected-visible-nodes ::selected-visible-nodes)
(def one-selected-node ::one-selected-node)
(def selected-edges ::selected-edges)
(def selected-visible-edges ::selected-visible-edges)
(def one-selected-edge ::one-selected-edge)
(def one-selected-visible-edge ::one-selected-visible-edge)
;; explicitly viewed connector / map-profile
(def viewed ::viewed)

;;(def main-entity-id ::main-entity-id)
(def main-map-profile-id ::main-map-profile-id)
(def graph-entities ::graph-entities)
(def map-profiles ::map-profiles)
(def map-profile-ids-set ::map-profile-ids-set)

(def graph-references ::graph-references)
(def connectors ::connectors)
(def connectors-of-type ::connectors-of-type)
(def connector-ids-set ::connector-ids-set)
(def connection-tuples ::connection-tuples)
(def connection-tuples-by-node-id ::connection-tuples-by-node-id)

(def all-named-views ::all-named-views)
;; for this main-map-profile:
(def named-views ::named-views)

(def all-view-configs ::all-view-configs)
(def view-configs ::view-configs)

;; visible nodes and edges (map-profile-ids and connector-ids) when
;; starting from main-map-profile-id and traversing through all reachable nodes
;; while obeying the hide-rules
(def visible-nodes-edges-ids ::visible-nodes-edges-ids)
(def visible-node-ids-set ::visible-node-ids-set)
(def visible-edge-ids-set ::visible-edge-ids-set)
(def preview-show-hide ::preview-show-hide)
;; traverse, ignore hide-rules:
(def reachable-nodes-edges-ids ::reachable-nodes-edges-ids)
(def reachable-edge-ids-set ::reachable-edge-ids-set)
(def reachable-connectors ::reachable-connectors)
(def reachable-connectors-of-type ::reachable-connectors-of-type)

;; graph-refs and connectors for a given graph-entity
(def graph-references-for ::graph-references-for)
(def graph-references-of-type-for ::graph-references-of-type-for)
(def connectors-for ::connectors-for)
(def connectors-of-type-for ::connectors-of-type-for)

(def connector-options ::connector-options)

(def hide-rules ::hide-rules)
(def ephemeral-hide-rules ::ephemeral-hide-rules)
;;preview when hiding/unhiding things:
;;(def visible-map-profile-ids ::visible-map-profile-ids) ;;<- renamed '-set-
(def visible-map-profile-ids-set ::visible-map-profile-ids-set)
;;edge as a pair [source-map-profile-id target-map-profile-id]
(def visible-connectors ::visible-connectors)
(def visible-connectors-of-type ::visible-connectors-of-type)
;; at start saved from view-config-entity into rf-state:
(def view-config ::view-config)
(def selected-system-config ::selected-system-config)
(def manual-positions? ::manual-positions?)
;; at start taken from view-config - updated on drag-end
(def view-positions ::view-positions)
(def ordered-visible-map-profile-ids ::ordered-visible-map-profile-ids)
(def known-positions ::known-positions)
(def top-right ::top-right)
(def unknown-position-nodes ::unknown-position-nodes)
;; unknown-position-nodes with resolved positions (i.e. listed in top-right)
(def resolved-positions ::resolved-unknown-positions)
(def positions ::positions)
(def visible-map-profiles ::visible-map-profiles)
(def defaults ::defaults)
(def default-path-type ::default-path-type)
;; more generic default value subscription:
(def default-value ::default-value)
(def background-variant ::background-variant)
;; connector-type specific default:
(def default-connector-path-type ::default-connector-path-type)
(def default-connector-source-handle ::default-connector-source-handle)
(def default-connector-target-handle ::default-connector-target-handle)
(def default-connector-line-type ::default-connector-line-type)
;; individually configured
(def connector-path-type ::connector-path-type)
(def connector-line-type ::connector-line-type)
;; connector-type specific:
(def connector-type-source-handles ::connector-type-source-handles)
(def connector-type-target-handles ::connector-type-target-handles)
(def connector-type-line-types ::connector-type-line-types)
;; connector-specific:
(def source-handles ::source-handles)
(def target-handles ::target-handles)
(def source-handle ::source-handle)
(def target-handle ::target-handle)
(def target-handles-by-id ::target-handles-by-id)
(def label-positions ::label-positions)
(def label-position ::label-position)
(def highlight-pep? ::highlight-pep?)
(def show-icons? ::show-icons?)
;; given to reagent-flow component:
(def flow-nodes ::flow-nodes)
(def flow-edges ::flow-edges)
(def hiding-view? ::hiding-view?)
(def map-profile-edits ::map-profile-edits)
(def connector-edits ::connector-edits)
(def edit-map-profile? ::edit-map-profile?)
(def edit-connector? ::edit-connector?)
;;(def hidden-preview-map-profile? ::hidden-preview-map-profile?)
(def hidden-preview-connector? ::hidden-preview-connector?)
(def settings ::settings)
(def settings? ::settings?)
(def delete-modal ::delete-modal)
(def create-connector-modal ::create-connector-modal)
(def validate-create-connector-modal ::validate-create-connector-modal)
(def validate-edit-graph-ref-modal ::validate-edit-graph-ref-modal)
(def edit-map-profile-modal ::edit-map-profile-modal)
(def edit-connector-modal ::edit-connector-modal)

(def node-menu ::node-menu)
(def edge-menu ::edge-menu)

(def reconnecting ::reconnecting)
(def reconnecting-node-id ::reconnecting-node-id)

;; Effects
(def auto-layout-watch ::auto-layout-watch)
(def remove-auto-layout-watch ::remove-auto-layout-watch)
(def fit-view-fx ::fit-view-fx)
(def export-as-excel-fx ::export-as-excel-fx)
(def to-local-storage-fx ::to-local-storage-fx)

;; Coeffects
(def tenant-schema-cofx ::tenant-schema-cofx)
(def session-entities-cofx ::session-entities-cofx)
(def timestamp-minutes-cofx ::timestamp-minutes-cofx)
(def local-storage-cofx ::local-storage-cofx)

;; Events
(def init-state ::init-state)
(def cleanup ::cleanup)
(def flow-initialized ::flow-initialized)
(def loaded-and-initialized ::loaded-and-initialized)
(def toggle-show-all-gref-sections ::toggle-show-all-gref-sections)
(def create-connector ::create-connector)
(def create-map-profile ::create-map-profile)
(def create-success ::create-success)
(def fetch-initial-data ::fetch-initial-data)
(def fetch-initial-data-success ::fetch-initial-data-success)
(def fetch-initial-data-error ::fetch-initial-data-error)
(def ws-sub-update ::ws-sub-update)
(def ws-sub-error ::ws-sub-error)
(def ws-sub-close ::ws-sub-close)
(def node-changes ::node-changes)
;;(def edge-changes ::edge-changes)
(def discard-positions ::discard-positions)
(def discard-selected-positions ::discard-selected-positions)
(def auto-layout-nodes ::auto-layout-nodes)
(def select-edges ::select-edges)
(def start-selection ::start-selection)
(def end-selection ::end-selection)
(def open-settings ::open-settings)
(def close-settings ::close-settings)
(def open-hiding-view ::open-hiding-view)
(def close-hiding-view ::close-hiding-view)
(def set-sidebar-mode ::set-sidebar-mode)
(def preview-hide-connector ::preview-hide-connector)
(def preview-hide-all ::preview-hide-all)
(def hide-connector ::hide-connector)
(def hide-all ::hide-all)
(def open-edit-map-profile ::open-edit-map-profile)
(def open-edit-connector ::open-edit-connector)
(def open-map-profile-curtain ::open-map-profile-curtain)
(def close-map-profile-curtain ::close-map-profile-curtain)
(def save-map-profile ::save-map-profile)
(def save-map-profile-success ::save-map-profile-success)
(def save-map-profile-error ::save-map-profile-error)
(def apply-hide-rules ::apply-hide-rules)
(def discard-hide-rules ::discard-hide-rules)
(def discard-map-profile ::discard-map-profile)
(def discard-connector ::discard-connector)
(def save-connector ::save-connector)
(def save-connector-success ::save-connector-success)
(def save-connector-error ::save-connector-error)
(def edit-map-profile ::edit-map-profile)
(def start-edit-map-profile ::start-edit-map-profile)
(def edit-connector ::edit-connector)
(def update-defaults ::update-defaults)
(def update-view-config ::update-view-config)
(def view-connector ::view-connector)
(def view-map-profile ::view-map-profile)
(def close-viewed-entity ::close-viewed-entity)
(def unselect-all ::unselect-all)
(def set-manual-positions ::set-manual-positions)
(def start-delete ::start-delete)
(def cancel-delete ::cancel-delete)
(def confirm-delete ::confirm-delete)
(def delete-success ::delete-success)
(def delete-error ::delete-error)
(def start-create-connector ::start-create-connector)
(def close-create-connector ::close-create-connector)
(def confirm-create-connector ::confirm-create-connector)
(def create-connector-success ::create-connector-success)
(def create-connector-error ::create-connector-error)
(def create-map-profile-error ::create-map-profile-error)
(def update-create-connector ::update-create-connector)
(def save-view-config ::save-view-config)
(def save-view-config-success ::save-view-config-success)
(def save-view-config-error ::save-view-config-error)
(def open-node-menu ::open-node-menu)
(def close-node-menu ::close-node-menu)
(def open-edge-menu ::open-edge-menu)
(def close-edge-menu ::close-edge-menu)
(def start-reconnect ::start-reconnect)
(def end-reconnect ::end-reconnect)
(def apply-reconnect ::apply-reconnect)
(def load-view-config ::load-view-config)
(def select-system-config ::select-system-config)
(def unselect-system-config ::unselect-system-config)
(def export-visible ::export-visible)
